import { useState } from 'react';
import { Inbox, MoreVertical, Pause, Pencil, Play, Plus, RefreshCw, Trash2 } from 'lucide-react';
import type { MailAccountView } from '@nlinks/shared';
import { useMailAccountMutations, useMailAccounts } from '@/lib/queries';
import { formatDuration, formatNumber, formatRelative } from '@/lib/format';
import { MailboxForm, type MailboxFormValues } from '@/components/MailboxForm';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  humanizeStatus,
  statusTone,
  useDismissOnOutsideClick,
  useToast,
} from '@/components/ui';

function MailboxCard({
  account,
  onEdit,
  onDelete,
  onSync,
  onToggleSync,
  busy,
}: {
  account: MailAccountView;
  onEdit: () => void;
  onDelete: () => void;
  onSync: () => void;
  onToggleSync: () => void;
  busy: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useDismissOnOutsideClick<HTMLDivElement>(menuOpen, () => setMenuOpen(false));
  const { stats } = account;

  return (
    <Card className="flex flex-col">
      <div className="flex items-start justify-between gap-3 border-b border-surface-border p-5">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-slate-100">{account.label}</h3>
          <p className="mt-0.5 truncate text-xs text-slate-500">{account.emailAddress}</p>
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <Badge tone={account.syncEnabled ? statusTone(account.syncStatus) : 'neutral'}>
              {account.syncEnabled ? humanizeStatus(account.syncStatus) : 'Paused'}
            </Badge>
            <Badge tone="neutral">{account.provider}</Badge>
            {account.folder !== 'INBOX' ? <Badge tone="neutral">{account.folder}</Badge> : null}
          </div>
        </div>

        <div className="relative shrink-0" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-surface-hover hover:text-slate-200"
            aria-label="Mailbox actions"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen ? (
            <div className="absolute right-0 z-20 mt-1 w-44 animate-fade-in overflow-hidden rounded-lg border border-surface-border bg-surface-overlay shadow-2xl">
              <button
                onClick={() => { setMenuOpen(false); onEdit(); }}
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm text-slate-300 transition hover:bg-surface-hover"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
                Edit
              </button>
              <button
                onClick={() => { setMenuOpen(false); onToggleSync(); }}
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm text-slate-300 transition hover:bg-surface-hover"
              >
                {account.syncEnabled ? (
                  <>
                    <Pause className="h-3.5 w-3.5" aria-hidden />
                    Pause sync
                  </>
                ) : (
                  <>
                    <Play className="h-3.5 w-3.5" aria-hidden />
                    Resume sync
                  </>
                )}
              </button>
              <button
                onClick={() => { setMenuOpen(false); onDelete(); }}
                className="flex w-full items-center gap-2.5 border-t border-surface-border px-3.5 py-2.5 text-sm text-red-400 transition hover:bg-red-500/10"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Remove
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {account.lastError && account.syncStatus !== 'OK' ? (
        <div className="border-b border-surface-border px-5 py-3">
          <Alert severity={account.syncStatus === 'AUTH_FAILED' ? 'error' : 'warning'}>
            <p className="break-anywhere text-xs">{account.lastError}</p>
          </Alert>
        </div>
      ) : null}

      <dl className="grid flex-1 grid-cols-2 gap-x-4 gap-y-3.5 p-5 text-sm">
        <div>
          <dt className="text-xs text-slate-500">Messages</dt>
          <dd className="mt-0.5 font-medium tabular-nums text-slate-200">
            {formatNumber(stats.messagesTotal)}
            {stats.messages24h > 0 ? (
              <span className="ml-1.5 text-xs font-normal text-emerald-400">+{stats.messages24h} today</span>
            ) : null}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Links found</dt>
          <dd className="mt-0.5 font-medium tabular-nums text-slate-200">{formatNumber(stats.linksFound)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Visited</dt>
          <dd className="mt-0.5 font-medium tabular-nums text-emerald-400">{formatNumber(stats.visitsSuccess)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Failed</dt>
          <dd className="mt-0.5 font-medium tabular-nums text-red-400">{formatNumber(stats.visitsFailed)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Avg visit time</dt>
          <dd className="mt-0.5 font-medium tabular-nums text-slate-300">{formatDuration(stats.avgDurationMs)}</dd>
        </div>
      </dl>

      <div className="flex items-center justify-between gap-3 border-t border-surface-border px-5 py-3">
        <span className="truncate text-xs text-slate-600">Synced {formatRelative(account.lastSyncAt)}</span>
        <Button
          size="sm"
          variant="secondary"
          onClick={onSync}
          loading={busy}
          disabled={!account.syncEnabled}
          icon={<RefreshCw className="h-3.5 w-3.5" />}
        >
          Sync
        </Button>
      </div>
    </Card>
  );
}

export function MailboxesPage() {
  const { data: accounts = [], isLoading } = useMailAccounts();
  const { create, update, remove, sync } = useMailAccountMutations();
  const { push } = useToast();

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<MailAccountView | null>(null);
  const [deleting, setDeleting] = useState<MailAccountView | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const handleCreate = async (values: MailboxFormValues) => {
    await create.mutateAsync({ ...values, username: values.username || values.emailAddress });
    push('success', `${values.label} connected.`);
    setAdding(false);
  };

  const handleUpdate = async (values: MailboxFormValues) => {
    if (!editing) return;
    // Omit the password entirely when left blank, so the stored credential is
    // preserved rather than being overwritten with an empty string.
    const body: Record<string, unknown> = {
      label: values.label,
      imapHost: values.imapHost,
      imapPort: values.imapPort,
      imapSecure: values.imapSecure,
      username: values.username || values.emailAddress,
      folder: values.folder,
      syncEnabled: values.syncEnabled,
      /**
       * Sent on every edit. Leaving it out meant the date picker in this dialog
       * was decorative: the form collected it and the API accepted it, but the
       * value never left the browser, so moving the start date back to pull in
       * older mail silently did nothing.
       */
      syncFromDate: values.syncFromDate,
    };
    if (values.password) body.password = values.password;

    await update.mutateAsync({ id: editing.id, body });
    push('success', `${values.label} updated.`);
    setEditing(null);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.id);
      push('success', `${deleting.label} removed. Its history is kept for reporting.`);
    } catch {
      push('error', 'Could not remove that mailbox.');
    } finally {
      setDeleting(null);
    }
  };

  const handleSync = async (account: MailAccountView) => {
    setSyncingId(account.id);
    try {
      await sync.mutateAsync(account.id);
      push('success', `Sync queued for ${account.label}.`);
    } catch {
      push('error', 'Could not queue that sync.');
    } finally {
      setSyncingId(null);
    }
  };

  const handleToggleSync = async (account: MailAccountView) => {
    try {
      await update.mutateAsync({ id: account.id, body: { syncEnabled: !account.syncEnabled } });
      push('success', account.syncEnabled ? `${account.label} paused.` : `${account.label} resumed.`);
    } catch {
      push('error', 'Could not change the sync setting.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">Mailboxes</h1>
          <p className="mt-1 text-sm text-slate-500">
            {accounts.length} connected · credentials are encrypted at rest.
          </p>
        </div>
        <Button onClick={() => setAdding(true)} icon={<Plus className="h-4 w-4" />}>
          Add mailbox
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton h-80 rounded-xl" />
          ))}
        </div>
      ) : accounts.length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {accounts.map((account) => (
            <MailboxCard
              key={account.id}
              account={account}
              busy={syncingId === account.id}
              onEdit={() => setEditing(account)}
              onDelete={() => setDeleting(account)}
              onSync={() => void handleSync(account)}
              onToggleSync={() => void handleToggleSync(account)}
            />
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<Inbox className="h-6 w-6" />}
            title="No mailboxes connected"
            description="Add the address that receives the mail you want monitored. NLinks will start checking it within minutes."
            action={
              <Button onClick={() => setAdding(true)} icon={<Plus className="h-4 w-4" />}>
                Add your first mailbox
              </Button>
            }
          />
        </Card>
      )}

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Connect a mailbox"
        description="Credentials are verified before they are saved."
        size="lg"
      >
        <MailboxForm onSubmit={handleCreate} onCancel={() => setAdding(false)} submitting={create.isPending} />
      </Modal>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={`Edit ${editing?.label ?? ''}`}
        description="Changing any connection setting re-verifies the mailbox."
        size="lg"
      >
        {editing ? (
          <MailboxForm
            existing={editing}
            onSubmit={handleUpdate}
            onCancel={() => setEditing(null)}
            submitting={update.isPending}
            submitLabel="Save changes"
          />
        ) : null}
      </Modal>

      <Modal
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="Remove this mailbox?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={remove.isPending}>
              Remove mailbox
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-400">
          <span className="font-medium text-slate-200">{deleting?.label}</span> will stop syncing and its stored
          password will be erased. Messages and visit history already collected are kept so your reports stay
          accurate.
        </p>
      </Modal>
    </div>
  );
}
